import { FeatureCollection } from 'geojson';
import { reverse } from 'lodash';
import {
  filterReportItems,
  formatSoilName,
  getMapUnitKeyFromName,
  getNameToMapUnitKeyMap,
  validateReport,
  WrappedColumn,
} from '../../utils/soils';
import { RawSection, Table } from '../../types/soils';
import { asPercentage } from '../../utils/formatters';
import { bucketColors, notRatedLegendItem } from '../../utils/mapLegend';

const HYDROLOGIC_TITLE = 'Hydrologic Soil Group and Surface Runoff';
const HYDROLOGIC_LEGEND = [
  { hexCode: bucketColors[0], label: 'Negligible' },
  { hexCode: bucketColors[1], label: 'Very low' },
  { hexCode: bucketColors[2], label: 'Low' },
  { hexCode: bucketColors[3], label: 'Medium' },
  { hexCode: bucketColors[4], label: 'High' },
  { hexCode: '#8000FF', label: 'Very high' },
  notRatedLegendItem,
];
const percentMapUnit = 'percentMapUnit';
const surfaceRunoff = 'surfaceRunoff';
const hydrologicSoilGroup = 'hydrologicSoilGroup';

interface RunoffMapAccumulator {
  runoffMap: { [mapUnitKey: string]: string };
  mapUnitKey: Nullable<string>;
  runoffValues: string[];
}

const cols = [
  {
    accessor: 'name',
    displayString: 'Map unit',
    format: formatSoilName,
    style: WrappedColumn,
  },
  {
    accessor: percentMapUnit,
    displayString: 'Percent of map unit',
    format: asPercentage,
  },
  { accessor: surfaceRunoff, displayString: 'Surface runoff' },
  { accessor: hydrologicSoilGroup, displayString: 'Hydrologic soil group' },
];

const columnSpan = (nameOnly: boolean) => ({
  colSpan: nameOnly ? { name: 4 } : undefined,
  skipCols: nameOnly
    ? [percentMapUnit, surfaceRunoff, hydrologicSoilGroup]
    : undefined,
});

const getItems = ({
  data,
  report: rawReport,
}: {
  data: FeatureCollection;
  report?: RawSection;
}) => {
  const report = validateReport(rawReport, HYDROLOGIC_TITLE);
  if (report == null) return [];
  if (rawReport == null) return [];
  const items = report.section.flatMap((section) =>
    section.table.flatMap((table) => [
      {
        key: table.title,
        name: table.title,
        style: { backgroundColor: '#e8e8e8' },
        ...columnSpan(true),
      },
      ...table.tbody.tr
        .slice(1)
        .map((row) => {
          return row.td.map((v) => (typeof v === 'string' ? v : v.para));
        })
        .map((td, i) => {
          const nameOnly = td.slice(1).every((v) => v === '');
          return {
            key: `${table.title}:${i}`,
            name: td[0],
            [percentMapUnit]: td[1],
            [surfaceRunoff]: td[2],
            [hydrologicSoilGroup]: td[3],
            ...columnSpan(nameOnly),
          };
        }),
    ]),
  );
  return filterReportItems(items, data);
};

const getLegend = () => HYDROLOGIC_LEGEND;

const updateRunoffMap = ({
  runoffMap,
  mapUnitKey,
  runoffValues,
}: RunoffMapAccumulator) => {
  if (!mapUnitKey) return runoffMap;
  const legend = reverse(HYDROLOGIC_LEGEND);
  const legendItem = legend.find(({ label }) => runoffValues.includes(label));
  return {
    ...runoffMap,
    [mapUnitKey]: legendItem?.hexCode || notRatedLegendItem.hexCode,
  };
};

const reduceTableSurfaceRunoff = (nameToKeyMap: { [key: string]: string }) => (
  table: Table,
) => {
  const tableResults = table.tbody.tr
    .slice(1)
    .map((row) => {
      return row.td.map((v) => (typeof v === 'string' ? v : v.para));
    })
    .reduce(
      ({ runoffMap, mapUnitKey, runoffValues }: RunoffMapAccumulator, td) => {
        const nameOnly = td.slice(1).every((v) => v === '');
        if (nameOnly) {
          return {
            runoffMap: updateRunoffMap({
              runoffMap,
              mapUnitKey,
              runoffValues,
            }),
            mapUnitKey: getMapUnitKeyFromName(td[0], nameToKeyMap),
            runoffValues: [],
          };
        }
        return {
          runoffMap,
          mapUnitKey,
          runoffValues: [...runoffValues, td[2]],
        };
      },
      {
        runoffMap: {},
        mapUnitKey: null,
        runoffValues: [],
      },
    );
  return Object.entries(updateRunoffMap(tableResults));
};
const getColorKey = ({
  data,
  report: rawReport,
}: {
  data: FeatureCollection;
  report?: RawSection;
}) => {
  const report = validateReport(rawReport, HYDROLOGIC_TITLE);
  if (report == null) return {};
  const nameToKeyMap = getNameToMapUnitKeyMap(data);
  const surfaceRunoffEntries = report.section.flatMap((section) =>
    section.table.flatMap(reduceTableSurfaceRunoff(nameToKeyMap)),
  );
  return Object.fromEntries(surfaceRunoffEntries);
};

export const HYDROLOGIC_SOIL_GROUP = {
  key: HYDROLOGIC_TITLE,
  name: HYDROLOGIC_TITLE,
  reportId: '474',
  reportShortFormData: {
    reportid: '474',
    Includeminorsoils: 'false',
    Displaynationalmapunitsymbols: 'false',
  },
  cols,
  getColorKey,
  getItems,
  getLegend,
  units: 'maximum surface runoff',
};
